/**
 * Utility to identify bottlenecks in loading large datasets
 */
export const LoadingMonitor = {
  config: {
    enabled: process.env.NODE_ENV !== "production",
    logToConsole: true,
    warnThreshold: 1000,
    slowThreshold: 3000,
    criticalThreshold: 5000,
    filterPatterns: [], // Any patterns we want to ignore in reporting
  },

  timers: {},

  // Performance entries storage - keeps the most recent operations for analysis
  entries: [],
  maxEntries: 50,

  /**
   * Configure the monitor
   * @param {Object} options - Configuration options
   * @returns {Object} - This instance for chaining
   */
  configure(options = {}) {
    this.config = { ...this.config, ...options };
    return this;
  },

  /**
   * Start timing an operation
   * @param {string} label - Name of the operation
   * @param {Object} metadata - Additional context data
   * @returns {Object} - This instance for chaining
   */
  start(label, metadata = {}) {
    if (!this.config.enabled) return this;

    // Skip if this matches a filter pattern
    if (this.shouldFilter(label)) return this;

    this.timers[label] = {
      start: performance.now(),
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
      },
    };

    return this;
  },

  /**
   * End timing an operation
   * @param {string} label - Name of the operation
   * @returns {number} - Duration in milliseconds or 0 if timer not found
   */
  end(label) {
    if (!this.config.enabled || !this.timers[label]) return 0;

    const end = performance.now();
    const timer = this.timers[label];
    const duration = end - timer.start;

    // Store in entries for analysis
    this.addEntry(label, duration, timer.metadata);

    // Log to console based on thresholds
    if (this.config.logToConsole) {
      this.logOperation(label, duration, timer.metadata);
    }

    // Clean up
    delete this.timers[label];

    return duration;
  },

  /**
   * Check if a label should be filtered out
   * @param {string} label - Operation label
   * @returns {boolean} - True if should be filtered
   */
  shouldFilter(label) {
    return this.config.filterPatterns.some((pattern) => {
      return typeof pattern === "string"
        ? label.includes(pattern)
        : pattern.test(label);
    });
  },

  /**
   * Add an entry to the performance log
   * @param {string} label - Operation label
   * @param {number} duration - Duration in ms
   * @param {Object} metadata - Operation metadata
   */
  addEntry(label, duration, metadata) {
    this.entries.unshift({
      label,
      duration,
      timestamp: new Date(),
      metadata,
    });

    // Trim entries if needed
    if (this.entries.length > this.maxEntries) {
      this.entries = this.entries.slice(0, this.maxEntries);
    }
  },

  /**
   * Log operation to console with appropriate styling
   * @param {string} label - Operation label
   * @param {number} duration - Duration in ms
   * @param {Object} metadata - Operation metadata
   */
  logOperation(label, duration, metadata) {
    // Determine severity
    let logMethod = "log";
    let icon = "🔍";

    if (duration > this.config.criticalThreshold) {
      logMethod = "error";
      icon = "🔥";
    } else if (duration > this.config.slowThreshold) {
      logMethod = "warn";
      icon = "⚠️";
    } else if (duration > this.config.warnThreshold) {
      logMethod = "warn";
      icon = "⏱️";
    }

    // Format duration
    const formattedDuration = duration.toFixed(1).padStart(7);

    // Create label with context
    let operationLabel = label;

    // Add metadata context if available
    const context = [];
    if (metadata.component) context.push(`component: ${metadata.component}`);
    if (metadata.items) context.push(`items: ${metadata.items}`);
    if (metadata.route) context.push(`route: ${metadata.route}`);

    const contextStr = context.length ? ` (${context.join(", ")})` : "";

    // Log to console
    console[logMethod](
      `${icon} ${formattedDuration}ms | ${operationLabel}${contextStr}`
    );

    // For critical issues, log more details
    if (duration > this.config.criticalThreshold && metadata) {
      console.group("Details:");

      // Filter out standard metadata properties
      const detailedMetadata = { ...metadata };
      delete detailedMetadata.component;
      delete detailedMetadata.items;
      delete detailedMetadata.route;

      if (Object.keys(detailedMetadata).length > 0) {
        console[logMethod](detailedMetadata);
      }

      console.groupEnd();
    }
  },

  /**
   * Get analysis of slow operations
   * @param {number} [threshold=1000] - Minimum duration to include
   * @param {number} [limit=10] - Maximum number of entries to include
   * @returns {Array} - Sorted array of slow operations
   */
  getSlowOperations(threshold = 1000, limit = 10) {
    const slowOps = this.entries
      .filter((entry) => entry.duration >= threshold)
      .sort((a, b) => b.duration - a.duration)
      .slice(0, limit);

    return slowOps;
  },

  /**
   * Log a summary of the slowest operations
   * @param {number} [minThreshold=1000] - Minimum duration to include
   * @param {number} [limit=5] - Number of operations to show
   */
  logSlowestOperations(minThreshold = 1000, limit = 5) {
    const slowOps = this.getSlowOperations(minThreshold, limit);

    if (slowOps.length === 0) {
      console.log("No slow operations recorded.");
      return;
    }

    console.group("🐢 Slowest Operations:");

    slowOps.forEach((op, index) => {
      console.log(
        `${index + 1}. ${op.duration.toFixed(1).padStart(7)}ms | ${op.label}`
      );
    });

    console.groupEnd();
  },

  /**
   * Group operations by category and analyze
   * @returns {Object} - Analysis by operation category
   */
  analyzeByCategory() {
    const categories = {};

    this.entries.forEach((entry) => {
      // Extract category from label (e.g., "loadAuctions" -> "load")
      const category = entry.label.match(/^([a-zA-Z]+)/)?.[1] || "other";

      if (!categories[category]) {
        categories[category] = {
          count: 0,
          totalDuration: 0,
          operations: [],
        };
      }

      categories[category].count++;
      categories[category].totalDuration += entry.duration;
      categories[category].operations.push(entry);
    });

    // Calculate averages and find slowest ops
    Object.keys(categories).forEach((cat) => {
      const category = categories[cat];
      category.avgDuration = category.totalDuration / category.count;
      category.slowestOp = category.operations.reduce(
        (prev, current) => (prev.duration > current.duration ? prev : current),
        { duration: 0 }
      );

      // Clean up
      delete category.operations;
    });

    return categories;
  },

  /**
   * Log category analysis
   */
  logCategoryAnalysis() {
    const analysis = this.analyzeByCategory();

    console.group("📊 Operation Categories:");

    Object.keys(analysis)
      .sort((a, b) => analysis[b].totalDuration - analysis[a].totalDuration)
      .forEach((category) => {
        const data = analysis[category];
        console.log(
          `${category}: ${data.count} ops, avg ${data.avgDuration.toFixed(
            1
          )}ms, ` +
            `slowest: ${
              data.slowestOp.label
            } (${data.slowestOp.duration.toFixed(1)}ms)`
        );
      });

    console.groupEnd();
  },

  /**
   * Create a wrapper for async methods in Vue components
   * @param {string} label - Base label for the operation
   * @returns {Function} - Decorator function for methods
   */
  vueMethodWrapper(label) {
    return (target, key, descriptor) => {
      const original = descriptor.value;

      descriptor.value = async function (...args) {
        const componentName = this.$options?.name || "UnknownComponent";
        const fullLabel = `${componentName}.${label || key}`;

        // Get relevant component data for context
        const metadata = {
          component: componentName,
          route: this.$route?.path,
        };

        // Try to determine data size if available
        const dataProperties = [
          "items",
          "auctions",
          "products",
          "inventory_listings",
        ];
        for (const prop of dataProperties) {
          if (this[prop] && Array.isArray(this[prop])) {
            metadata.items = this[prop].length;
            break;
          }
        }

        LoadingMonitor.start(fullLabel, metadata);

        try {
          return await original.apply(this, args);
        } finally {
          LoadingMonitor.end(fullLabel);
        }
      };

      return descriptor;
    };
  },

  /**
   * Measure performance of an async function
   * @param {string} label - Operation label
   * @param {Function} fn - Function to measure
   * @param {Object} metadata - Additional context data
   * @returns {Promise} - Promise resolving to function result
   */
  async measure(label, fn, metadata = {}) {
    this.start(label, metadata);

    try {
      return await fn();
    } finally {
      this.end(label);
    }
  },
};

export default LoadingMonitor;
